import { supabaseService } from './supabase.service';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface RoomUser {
  id: string;
  name: string;
  joinedAt: number;
  isActive: boolean;
}

export interface GestureBroadcast {
  userId: string;
  userName: string;
  gesture: string;
  confidence: number;
  timestamp: number;
}

export interface EmergencyAlert {
  userId: string;
  userName: string;
  type: 'distress' | 'fall' | 'help';
  timestamp: number;
  location?: string;
}

export interface ChatMessage {
  userId: string;
  userName: string;
  message: string;
  timestamp: number;
}

class RealtimeService {
  private currentRoom: string | null = null;
  private currentChannel: RealtimeChannel | null = null;
  private userId: string | null = null;
  private userName: string | null = null;
  private presenceInterval: number | null = null;

  async joinRoom(
    roomId: string,
    userId: string,
    userName: string,
    callbacks: {
      onUserJoined?: (user: RoomUser) => void;
      onUserLeft?: (userId: string) => void;
      onGesture?: (gesture: GestureBroadcast) => void;
      onEmergency?: (alert: EmergencyAlert) => void;
      onChat?: (message: ChatMessage) => void;
      onPresenceUpdate?: (users: RoomUser[]) => void;
    }
  ): Promise<void> {
    try {
      // Leave current room if any
      if (this.currentRoom) {
        await this.leaveRoom();
      }

      this.currentRoom = roomId;
      this.userId = userId;
      this.userName = userName;

      // Create channel
      const channel = supabaseService.createChannel(`room:${roomId}`);
      this.currentChannel = channel;

      // Subscribe to presence
      channel.on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users: RoomUser[] = [];
        
        Object.keys(state).forEach(key => {
          const presences = state[key];
          if (presences && presences.length > 0) {
            const presence = presences[0] as any;
            users.push({
              id: presence.user_id,
              name: presence.user_name,
              joinedAt: presence.joined_at,
              isActive: true
            });
          }
        });

        callbacks.onPresenceUpdate?.(users);
      });

      channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
        const presence = newPresences[0] as any;
        callbacks.onUserJoined?.({
          id: presence.user_id,
          name: presence.user_name,
          joinedAt: presence.joined_at,
          isActive: true
        });
      });

      channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        const presence = leftPresences[0] as any;
        callbacks.onUserLeft?.(presence.user_id);
      });

      // Subscribe to broadcast events
      channel.on('broadcast', { event: 'gesture' }, ({ payload }) => {
        callbacks.onGesture?.(payload as GestureBroadcast);
      });

      channel.on('broadcast', { event: 'emergency' }, ({ payload }) => {
        callbacks.onEmergency?.(payload as EmergencyAlert);
      });

      channel.on('broadcast', { event: 'chat' }, ({ payload }) => {
        callbacks.onChat?.(payload as ChatMessage);
      });

      // Subscribe to channel
      await channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Track presence
          await channel.track({
            user_id: userId,
            user_name: userName,
            joined_at: Date.now(),
            online_at: Date.now()
          });

          // Update presence every 30 seconds
          this.presenceInterval = window.setInterval(async () => {
            await channel.track({
              user_id: userId,
              user_name: userName,
              joined_at: Date.now(),
              online_at: Date.now()
            });
          }, 30000);
        }
      });

    } catch (error) {
      console.error('Failed to join room:', error);
      throw error;
    }
  }

  async leaveRoom(): Promise<void> {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
      this.presenceInterval = null;
    }

    if (this.currentRoom) {
      await supabaseService.removeChannel(`room:${this.currentRoom}`);
      this.currentRoom = null;
      this.currentChannel = null;
      this.userId = null;
      this.userName = null;
    }
  }

  async broadcastGesture(gesture: string, confidence: number): Promise<void> {
    if (!this.currentChannel || !this.userId || !this.userName) {
      throw new Error('Not in a room');
    }

    await this.currentChannel.send({
      type: 'broadcast',
      event: 'gesture',
      payload: {
        userId: this.userId,
        userName: this.userName,
        gesture,
        confidence,
        timestamp: Date.now()
      }
    });
  }

  async sendEmergencyAlert(type: 'distress' | 'fall' | 'help', location?: string): Promise<void> {
    if (!this.currentChannel || !this.userId || !this.userName) {
      throw new Error('Not in a room');
    }

    await this.currentChannel.send({
      type: 'broadcast',
      event: 'emergency',
      payload: {
        userId: this.userId,
        userName: this.userName,
        type,
        timestamp: Date.now(),
        location
      }
    });
  }

  async sendChatMessage(message: string): Promise<void> {
    if (!this.currentChannel || !this.userId || !this.userName) {
      throw new Error('Not in a room');
    }

    await this.currentChannel.send({
      type: 'broadcast',
      event: 'chat',
      payload: {
        userId: this.userId,
        userName: this.userName,
        message,
        timestamp: Date.now()
      }
    });
  }

  getCurrentRoom(): string | null {
    return this.currentRoom;
  }

  isInRoom(): boolean {
    return this.currentRoom !== null;
  }
}

export const realtimeService = new RealtimeService();
